 import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Container,
  Grid,
  alpha,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowForward } from "@mui/icons-material";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const BRAND_COLOR = "#1976D2";
const ITEMS_PER_PAGE = 15;

export default function AllTutor() {
  const location = useLocation();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/students/approved-courses`
        );

        setTutors(res.data.data || []);
      } catch (err) {
        console.error("❌ Failed to fetch tutors:", err);
        setSnackbar({
          open: true,
          message: "Failed to load tutors.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTutors();
  }, []);

  const courseQuery = new URLSearchParams(location.search)
    .get("course")
    ?.trim()
    .toLowerCase() || "";

  const filteredTutors = courseQuery
    ? tutors.filter((tutor) =>
        String(tutor.course_name || "")
          .toLowerCase()
          .includes(courseQuery)
      )
    : tutors;

  useEffect(() => {
    setPage(1);
  }, [courseQuery]);

  // ✅ Pagination logic
  const totalPages = Math.max(1, Math.ceil(filteredTutors.length / ITEMS_PER_PAGE));
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const currentTutors = filteredTutors.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handleNext = () => {
    if (page < totalPages) setPage((prev) => prev + 1);
  };

  const handlePrev = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  // ✅ Limit to ~15 words
  const limitWords = (text, count = 15) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > count
      ? words.slice(0, count).join(" ") + "..."
      : text;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress size={60} sx={{ color: BRAND_COLOR }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8, mt: 6 }}>
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={700}>
          Find Your Perfect Tutor
        </Typography>
        <Typography color="text.secondary">
          Browse all available expert tutors and start learning today.
        </Typography>
      </Box>

      {/* Tutors */}
      {filteredTutors.length === 0 ? (
        <Typography textAlign="center" color="text.secondary">
          {courseQuery
            ? "No approved tutors found for that course yet."
            : "No tutors available yet."}
        </Typography>
      ) : (
        <>
          <Grid container spacing={4} justifyContent="center">
            {currentTutors.map((tutor) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4 }}
                key={tutor.id}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <TutorCard tutor={tutor} limitWords={limitWords} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Box
            sx={{
              mt: 6,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePrev}
              disabled={page === 1}
            >
              Prev
            </Button>

            <Typography sx={{ alignSelf: "center" }}>
              Page {page} of {totalPages}
            </Typography>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={page === totalPages}
              sx={{ backgroundColor: BRAND_COLOR }}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

/* CARD */
function TutorCard({ tutor, limitWords }) {
  return (
    <Card
      sx={{
        minWidth: 320,
        maxWidth: 340,
        borderRadius: 5,
        textAlign: "center",
        p: 3,
        transition: "0.3s",
        "&:hover": {
          transform: "translateY(-6px)",
        },
      }}
    >
      <Avatar
        src={
          tutor.profile_img
            ? `${BASE_URL}/uploads/tutors/${tutor.profile_img}`
            : "/images/default-avatar.png"
        }
        sx={{
          width: 90,
          height: 90,
          mx: "auto",
          mb: 2,
          border: `3px solid ${alpha(BRAND_COLOR, 0.5)}`,
        }}
      />

      <Typography variant="h6" fontWeight={700}>
        {tutor.tutor_name}
      </Typography>

      <Typography variant="body2" sx={{ mb: 1, color: "#64748b" }}>
        {tutor.course_name || "General Tutor"}
      </Typography>

      <Typography variant="body2" sx={{ color: "#64748b", minHeight: 50 }}>
        {limitWords(tutor.course_description)}
      </Typography>

      <Button
        variant="contained"
        component={Link}
        to={`/tutor-details/${tutor.id}`}
        endIcon={<ArrowForward />}
        fullWidth
        sx={{
          mt: 3,
          borderRadius: 3,
          backgroundColor: BRAND_COLOR,
        }}
      >
        Enroll Now
      </Button>
    </Card>
  );
}